// A minimal, Safari 10-compatible stand-in for the subset of `URLSearchParams`
// we use (get/set/delete/toString).
//
// We intentionally avoid the native `URLSearchParams`. On older browsers (e.g.
// iOS Safari) it is polyfilled with a Map-backed implementation, and where `Map`
// is itself polyfilled via `WeakMap`, calling `.set('uuid', value)` throws
// "WeakMap keys must be objects or non-registered symbols" (strings are not
// valid WeakMap keys). usage-tracker runs on arbitrary external pages, so this
// helper uses plain string parsing to stay agnostic to those polyfills.
//
// TODO: once traffic from browsers without native `URLSearchParams` (Safari < 10
// and the Map/WeakMap polyfill chain) is negligible, delete this file and use
// the native `URLSearchParams` directly.

// A single decoded query parameter, e.g. ['uuid', '1234'].

// Removes the leading "?" from a query string, if present ("?a=1" -> "a=1").
const stripLeadingQuestionMark = search => search.charAt(0) === '?' ? search.slice(1) : search;

// Decodes a single "key=value" segment into an [key, value] entry. A segment
// without "=" is treated as a key with an empty value ("flag" -> ['flag', '']).
const decodeSegment = segment => {
  const separatorIndex = segment.indexOf('=');
  const hasValue = separatorIndex !== -1;
  const key = hasValue ? segment.slice(0, separatorIndex) : segment;
  const value = hasValue ? segment.slice(separatorIndex + 1) : '';
  return [decodeURIComponent(key), decodeURIComponent(value)];
};

// Encodes a single [key, value] entry back into a "key=value" segment.
const encodeEntry = ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

// Parses a query string into decoded entries, ignoring any empty segments
// produced by a leading/trailing/double "&".
const parse = search => {
  const query = stripLeadingQuestionMark(search);
  if (!query) {
    return [];
  }
  return query.split('&').filter(Boolean).map(decodeSegment);
};
export const createSearchParams = (search = '') => {
  const entries = parse(search);
  const findEntry = name => entries.find(([key]) => key === name);
  return {
    get: name => {
      const entry = findEntry(name);
      return entry ? entry[1] : null;
    },
    set: (name, value) => {
      const entry = findEntry(name);
      if (entry) {
        entry[1] = value;
      } else {
        entries.push([name, value]);
      }
    },
    delete: name => {
      for (let index = entries.length - 1; index >= 0; index--) {
        if (entries[index][0] === name) {
          entries.splice(index, 1);
        }
      }
    },
    toString: () => entries.map(encodeEntry).join('&')
  };
};